import { Check, Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface HastNode {
  type: string;
  value?: string;
  children?: HastNode[];
}

function textOf(node: HastNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.value ?? '';
  return (node.children ?? []).map(textOf).join('');
}

/** Bloco de texto pronto para copiar (os prompts dos artigos). */
function CopyBlock({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-rule bg-paper p-3">
      <pre className="font-mono text-[13px] leading-5 whitespace-pre-wrap text-ink">{text}</pre>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={copy}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-control-border bg-sheet px-3 py-1 text-xs font-medium text-ink hover:border-ink"
        >
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          {copied ? t('help.copied') : t('help.copy')}
        </button>
      </div>
    </div>
  );
}

function TextLink({ href, children }: { href?: string; children?: ReactNode }) {
  const external = href?.startsWith('http');
  return (
    <a
      href={href}
      className="text-brand-text underline underline-offset-2"
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

const components: Components = {
  h2: ({ children }) => <h2 className="mt-4 text-lg font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-2 text-base font-semibold">{children}</h3>,
  p: ({ children }) => <p className="text-sm leading-6">{children}</p>,
  ul: ({ children }) => <ul className="ml-5 flex list-disc flex-col gap-1 text-sm">{children}</ul>,
  ol: ({ children }) => (
    <ol className="ml-5 flex list-decimal flex-col gap-1 text-sm">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-6">{children}</li>,
  a: ({ href, children }) => <TextLink href={href}>{children}</TextLink>,
  code: ({ children }) => (
    <code className="rounded bg-paper px-1 py-0.5 font-mono text-[13px]">{children}</code>
  ),
  pre: ({ node }) => <CopyBlock text={textOf(node as HastNode | undefined).replace(/\n$/, '')} />,
  table: ({ children }) => (
    <div className="overflow-x-auto rounded-lg border border-rule">
      <table className="w-full border-collapse text-left text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-rule bg-paper px-3 py-2 text-xs font-medium text-muted">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-rule px-3 py-2 align-top leading-6">{children}</td>
  ),
};

/**
 * Texto em Markdown (Ajuda e Central de Conhecimento), com o visual do sistema. Não aceita HTML
 * solto. `resolveLink` troca links relativos (ex.: outro documento .md) pelo endereço na
 * plataforma; sem endereço, o texto aparece sem link.
 */
export function Markdown({
  children,
  resolveLink,
}: {
  children: string;
  resolveLink?: (href: string) => string | null;
}) {
  const comps: Components = resolveLink
    ? {
        ...components,
        a: ({ href, children: label }) => {
          if (!href || href.startsWith('http') || href.startsWith('#')) {
            return <TextLink href={href}>{label}</TextLink>;
          }
          const to = resolveLink(href);
          return to ? (
            <a href={to} className="text-brand-text underline underline-offset-2">
              {label}
            </a>
          ) : (
            <span>{label}</span>
          );
        },
      }
    : components;
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={comps}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
